import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Row, Col, Button, Card, Radio, Select, Space, Typography, Spin, Alert, Tag } from 'antd';
import { RightOutlined, SunOutlined, ReloadOutlined } from '@ant-design/icons';
import { goodweService } from '../../services/api';

const { Option } = Select;
const { Text } = Typography;

const Step2Solar = ({ onNext, onBack, formData }) => {
    const [form] = Form.useForm();
    const [hasGoodwe, setHasGoodwe] = useState(formData?.hasGoodwe ?? true);
    const [goodwePlants, setGoodwePlants] = useState([]);
    const [loadingPlants, setLoadingPlants] = useState(false);

    // Carregar plantas Goodwe disponíveis
    const fetchGoodwePlants = async () => {
        try {
            setLoadingPlants(true);
            const response = await goodweService.getPlants();
            if (response.success && response.data) {
                setGoodwePlants(response.data);
            }
        } catch (error) {
            console.error('Erro ao carregar plantas Goodwe:', error);
        } finally {
            setLoadingPlants(false);
        }
    };

    useEffect(() => {
        fetchGoodwePlants();
    }, []);

    // Pré-preencher
    React.useEffect(() => {
        if (formData) {
            form.setFieldsValue(formData);
            if (formData.hasGoodwe !== undefined) {
                setHasGoodwe(formData.hasGoodwe);
            }
        }
    }, [formData, form]);

    const handleSubmit = async (values) => {
        const data = {
            ...values,
            hasGoodwe,
        };
        onNext(data);
    };

    return (
        <Card title={
            <span>
                <SunOutlined style={{ marginRight: '8px', color: '#FFB100' }} />
                Usina Solar / Inversor
            </span>
        }>
            <Alert
                message="Esta etapa é obrigatória"
                description="Todo site precisa ter pelo menos uma fonte de geração solar cadastrada."
                type="info"
                showIcon
                style={{ marginBottom: '24px' }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ hasGoodwe: true, ...formData }}
            >
                <Form.Item label="Como você quer cadastrar o inversor?">
                    <Radio.Group 
                        value={hasGoodwe} 
                        onChange={(e) => setHasGoodwe(e.target.value)}
                        buttonStyle="solid"
                    >
                        <Radio.Button value={true}>
                            Vincular com Planta Goodwe (Recomendado)
                        </Radio.Button>
                        <Radio.Button value={false}>
                            Cadastro Manual
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {hasGoodwe ? (
                    // OPÇÃO 1: Vincular com Goodwe
                    <div style={{ padding: '20px', backgroundColor: '#f0f5ff', borderRadius: '8px' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <div>
                                <Text strong>Selecione a planta Goodwe:</Text>
                                <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
                                    Plantas disponíveis na conta Goodwe configurada no sistema
                                </Text>
                            </div>

                            <Form.Item
                                name="goodwePlantId"
                                rules={[{ required: hasGoodwe, message: 'Selecione uma planta Goodwe' }]}
                            >
                                {loadingPlants ? (
                                    <Spin tip="Carregando plantas Goodwe..." />
                                ) : (
                                    <Select
                                        placeholder="Selecione a planta"
                                        size="large"
                                        showSearch
                                        optionFilterProp="children"
                                        notFoundContent="Nenhuma planta encontrada"
                                        dropdownRender={menu => (
                                            <>
                                                {menu}
                                                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
                                                    <Button 
                                                        type="link" 
                                                        icon={<ReloadOutlined />}
                                                        onClick={fetchGoodwePlants}
                                                        size="small"
                                                    >
                                                        Atualizar lista
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    >
                                        {goodwePlants.map((plant) => (
                                            <Option key={plant.id} value={plant.id}>
                                                <Space direction="vertical" size={0}>
                                                    <Text strong>{plant.name}</Text>
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                                        {plant.address} • {plant.capacity || '-'} kWp
                                                    </Text>
                                                    <Tag color={plant.status === 1 ? 'green' : 'default'} style={{ marginTop: '4px' }}>
                                                        {plant.status === 1 ? 'Online' : 'Offline'}
                                                    </Tag>
                                                </Space>
                                            </Option>
                                        ))}
                                    </Select>
                                )}
                            </Form.Item>

                            <Alert
                                message="Dados serão sincronizados automaticamente"
                                description="Ao vincular com uma planta Goodwe, os dados de geração, potência e status serão atualizados automaticamente em tempo real."
                                type="success"
                                showIcon
                            />
                        </Space>
                    </div>
                ) : (
                    // OPÇÃO 2: Cadastro Manual
                    <div style={{ padding: '20px', backgroundColor: '#fff7e6', borderRadius: '8px' }}>
                        <Alert
                            message="Cadastro manual"
                            description="Os dados precisarão ser atualizados manualmente. Recomendamos usar integração Goodwe quando possível."
                            type="warning"
                            showIcon
                            style={{ marginBottom: '16px' }}
                        />

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Nome do Inversor"
                                    name="inversorNome"
                                    rules={[{ required: !hasGoodwe, message: 'Nome é obrigatório' }]}
                                >
                                    <Input placeholder="Ex: Inversor Principal" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Número de Série"
                                    name="inversorSerial"
                                    rules={[{ required: !hasGoodwe, message: 'Número de série é obrigatório' }]}
                                >
                                    <Input placeholder="Ex: GW12345678" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Fabricante"
                                    name="inversorFabricante"
                                    rules={[{ required: !hasGoodwe, message: 'Fabricante é obrigatório' }]}
                                >
                                    <Input placeholder="Ex: Growatt, Fronius, SMA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Modelo"
                                    name="inversorModelo"
                                    rules={[{ required: !hasGoodwe, message: 'Modelo é obrigatório' }]}
                                >
                                    <Input placeholder="Ex: GW50K-MT" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Capacidade Nominal (kWp)"
                                    name="pvCapacity"
                                    rules={[{ required: !hasGoodwe, message: 'Capacidade é obrigatória' }]}
                                >
                                    <InputNumber 
                                        min={0} 
                                        step={0.1} 
                                        style={{ width: '100%' }} 
                                        placeholder="150.5"
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Data de Instalação"
                                    name="dataInstalacao"
                                >
                                    <Input type="date" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                )}

                <Form.Item style={{ marginBottom: 0, marginTop: '24px', textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onBack}>
                            Voltar
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit"
                            icon={<RightOutlined />}
                            size="large"
                            style={{ backgroundColor: '#FB8500', borderColor: '#FB8500' }}
                        >
                            Próximo: Bateria
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default Step2Solar;
